import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { CheckCircle, XCircle, Clock, AlertCircle, Save } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import BranchLabel from './ui/BranchLabel';
import DateInput from './ui/date-input';
import useBranchLabels from '../hooks/useBranchLabels';
import { todayString } from '../utils/appDateTime';

const Attendance = () => {
  const { user, canManageFeature } = useAuth();
  const isAdminMark = canManageFeature('attendance') && user?.role_slug !== 'teacher';
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [attendanceMeta, setAttendanceMeta] = useState({});
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedDate, setSelectedDate] = useState(todayString());
  const [loading, setLoading] = useState(false);
  const [primaryClassTeacherAbsent, setPrimaryClassTeacherAbsent] = useState(false);
  const { toast } = useToast();
  const { showBranchLabels, formatLabel } = useBranchLabels();

  const selectedClassObj = classes.find((c) => String(c.id) === String(selectedClass));
  const classUsesSections = (cls) => {
    if (!cls) return false;
    if (cls.uses_sections !== undefined && cls.uses_sections !== null) {
      return Number(cls.uses_sections) === 1;
    }
    return (cls.sections || []).length > 0;
  };
  const needsSection = classUsesSections(selectedClassObj);
  const teacherUserIdStr = String(user?.id || '');

  const canMarkSelectedSection = (() => {
    if (user?.role_slug !== 'teacher' || !selectedClassObj) return false;
    const cls = selectedClassObj;
    const secs = cls.sections || [];
    const uid = teacherUserIdStr;

    if (!needsSection) {
      if (String(cls.class_teacher_user_id || '') === uid) return true;
      if (
        String(cls.secondary_class_teacher_user_id || '') === uid &&
        primaryClassTeacherAbsent
      ) {
        return true;
      }
      return false;
    }

    if (!selectedSection) return false;
    const sec = secs.find((s) => String(s.id) === String(selectedSection));
    if (!sec) return false;

    const isClassLevelPrimary = String(cls.class_teacher_user_id || '') === uid;
    const isSectionPrimary = String(sec.class_teacher_user_id || '') === uid;
    if (isClassLevelPrimary || isSectionPrimary) return true;

    const hasSectionPrimary = secs.some((s) => String(s.class_teacher_user_id || '') === uid);
    const asSecondary =
      String(cls.secondary_class_teacher_user_id || '') === uid &&
      !isClassLevelPrimary &&
      !hasSectionPrimary;
    return asSecondary && primaryClassTeacherAbsent;
  })();

  const canMarkAttendance = isAdminMark || canMarkSelectedSection;
  const today = todayString();
  const isFutureDate = selectedDate > today;
  const canEditAttendance = canMarkAttendance && !isFutureDate;

  const isPrimaryClassTeacher =
    user?.role_slug === 'teacher' &&
    selectedClassObj &&
    (String(selectedClassObj.class_teacher_user_id || '') === teacherUserIdStr ||
      (selectedClassObj.sections || []).some(
        (s) => String(s.class_teacher_user_id || '') === teacherUserIdStr,
      ));
  const isSecondaryClassTeacher =
    user?.role_slug === 'teacher' &&
    selectedClassObj &&
    String(selectedClassObj.secondary_class_teacher_user_id || '') === teacherUserIdStr &&
    !isPrimaryClassTeacher;
  const teacherMarksThisClass =
    user?.role_slug === 'teacher' &&
    selectedClassObj &&
    (isPrimaryClassTeacher || isSecondaryClassTeacher);

  useEffect(() => { fetchClasses(); }, [user?.school_id]);

  useEffect(() => {
    const ready =
      selectedClass &&
      selectedDate &&
      (user?.role_slug !== 'teacher' || !needsSection || selectedSection);
    if (ready) {
      fetchStudentsAndAttendance();
    }
  }, [selectedClass, selectedSection, selectedDate, user?.school_id, user?.role_slug, needsSection]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => {
      fetchClasses();
      if (selectedClass && selectedDate) fetchStudentsAndAttendance();
    };
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [selectedClass, selectedDate, user?.school_id]);

  const fetchClasses = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const params = academicYearId ? { academic_year_id: academicYearId } : {};
      const response = await api.get('/classes', { params });
      const list = response.data.data || [];
      setClasses(list);
      if (selectedClass && !list.some((c) => String(c.id) === String(selectedClass))) {
        setSelectedClass('');
      }
    } catch (error) {
      console.error('Failed to fetch classes');
    }
  };

  useEffect(() => {
    // Reset section when class changes
    setSelectedSection('');
    setPrimaryClassTeacherAbsent(false);
  }, [selectedClass]);

  useEffect(() => {
    if (user?.role_slug !== 'teacher') return;
    if (!selectedClass) return;
    const cls = classes.find((c) => String(c.id) === String(selectedClass));
    const secs = cls?.sections || [];
    const uid = String(user?.id || '');
    const isClassLevelPrimary = cls && String(cls.class_teacher_user_id || '') === uid;
    const primarySecs = secs.filter((s) => String(s.class_teacher_user_id || '') === uid);
    const isSec =
      cls && String(cls.secondary_class_teacher_user_id || '') === uid && !isClassLevelPrimary && primarySecs.length === 0;
    if (isAdminMark || teacherMarksThisClass) {
      if (isClassLevelPrimary && secs.length >= 1) {
        setSelectedSection(String(secs[0].id));
      } else if (primarySecs.length === 1) {
        setSelectedSection(String(primarySecs[0].id));
      } else if (isSec && secs.length >= 1) {
        setSelectedSection(String(secs[0].id));
      }
    } else if (needsSection && secs.length >= 1) {
      setSelectedSection(String(secs[0].id));
    }
  }, [classes, selectedClass, user?.role_slug, user?.id, isAdminMark, teacherMarksThisClass, needsSection]);

  const fetchStudentsAndAttendance = async () => {
    setLoading(true);
    try {
      const academicYearId = getSelectedAcademicYearId();
      const params = {
        class_id: selectedClass,
        ...(selectedSection ? { section_id: selectedSection } : {}),
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      };
      const [studentsRes, attendanceRes] = await Promise.all([
        api.get('/students', { params }),
        api.get('/attendance', { params: { ...params, date: selectedDate } })
      ]);

      const studentsData = studentsRes.data.data || [];
      setStudents(studentsData);

      const attendanceMap = {};
      const metaMap = {};
      (attendanceRes.data.data || []).forEach(a => {
        attendanceMap[a.student_id] = a.status;
        metaMap[a.student_id] = {
          source: a.source || 'manual',
          first_punch_at: a.first_punch_at || null,
          last_punch_at: a.last_punch_at || null,
        };
      });

      if (canEditAttendance) {
        studentsData.forEach((s) => {
          if (!attendanceMap[s.id]) {
            attendanceMap[s.id] = 'absent';
          }
        });
      }

      setAttendance(attendanceMap);
      setAttendanceMeta(metaMap);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (studentId, status) => {
    setAttendance(prev => ({ ...prev, [studentId]: status }));
  };

  const handleSave = async () => {
    if (isFutureDate) {
      toast({
        title: 'Future date',
        description: 'Attendance cannot be marked for a future date',
        variant: 'destructive',
      });
      return;
    }
    try {
      const attendanceData = Object.entries(attendance).map(([student_id, status]) => ({
        student_id: parseInt(student_id),
        status,
      }));

      await api.post('/attendance/mark', {
        class_id: parseInt(selectedClass),
        section_id: selectedSection ? parseInt(selectedSection, 10) : undefined,
        date: selectedDate,
        attendance: attendanceData,
        ...(isSecondaryClassTeacher && primaryClassTeacherAbsent ? { primary_class_teacher_absent: true } : {}),
      });

      toast({ title: 'Success', description: 'Attendance saved successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save attendance', variant: 'destructive' });
    }
  };

  const statusOptions = [
    { value: 'present', label: 'Present', icon: CheckCircle, color: 'text-green-500' },
    { value: 'absent', label: 'Absent', icon: XCircle, color: 'text-red-500' },
    { value: 'late', label: 'Late', icon: Clock, color: 'text-amber-500' },
    { value: 'leave', label: 'Leave', icon: AlertCircle, color: 'text-blue-500' },
  ];

  const summary = {
    present: Object.values(attendance).filter(s => s === 'present').length,
    absent: Object.values(attendance).filter(s => s === 'absent').length,
    late: Object.values(attendance).filter(s => s === 'late').length,
    leave: Object.values(attendance).filter(s => s === 'leave').length,
  };

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Attendance</h1>
        <p className="text-muted-foreground">
          {canMarkAttendance ? 'Mark daily attendance' : 'View daily attendance for your classes'}
        </p>
      </div>

      {/* Filters */}
      <Card className="border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger className="w-full sm:w-48 bg-muted border-border text-foreground">
                <SelectValue placeholder="Select Class" />
              </SelectTrigger>
              <SelectContent className="bg-muted border-border">
                {classes.map(c => (
                  <SelectItem key={c.id} value={c.id.toString()}>{formatLabel(c.name, c)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {needsSection && (
            <Select value={selectedSection} onValueChange={setSelectedSection} disabled={!selectedClass}>
              <SelectTrigger className="w-full sm:w-48 bg-muted border-border text-foreground">
                <SelectValue placeholder="Select Section" />
              </SelectTrigger>
              <SelectContent className="bg-muted border-border">
                {(() => {
                  const cls = classes.find((c) => String(c.id) === String(selectedClass));
                  const secs = cls?.sections || [];
                  const visible =
                    teacherMarksThisClass
                      ? (() => {
                          const uid = String(user?.id || '');
                          const isClassLevelPrimary = String(cls?.class_teacher_user_id || '') === uid;
                          const primaryMatch = secs.filter((s) => String(s.class_teacher_user_id || '') === uid);
                          const asSecondary =
                            String(cls?.secondary_class_teacher_user_id || '') === uid &&
                            !isClassLevelPrimary &&
                            primaryMatch.length === 0;
                          if (isClassLevelPrimary || asSecondary) return secs;
                          return primaryMatch;
                        })()
                      : secs;
                  return visible.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>Section {s.name}</SelectItem>
                  ));
                })()}
              </SelectContent>
            </Select>
            )}
            <div className="flex flex-col gap-1">
              <DateInput
                value={selectedDate}
                max={today}
                onChange={setSelectedDate}
                className="px-3 py-2 bg-muted border border-border rounded-md text-foreground"
              />
              {isFutureDate ? (
                <p className="text-xs text-muted-foreground">Future dates are read-only.</p>
              ) : null}
            </div>
            {canEditAttendance && isSecondaryClassTeacher && (
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={primaryClassTeacherAbsent}
                  onChange={(e) => setPrimaryClassTeacherAbsent(e.target.checked)}
                  className="accent-blue-500"
                />
                Class teacher absent (substitute)
              </label>
            )}
            {canEditAttendance && (
            <Button
              onClick={handleSave}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={
                students.length === 0 ||
                !selectedClass ||
                (needsSection && !selectedSection) ||
                (isSecondaryClassTeacher && !primaryClassTeacherAbsent)
              }
            >
              <Save className="w-4 h-4 mr-2" /> Save Attendance
            </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      {students.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {statusOptions.map(opt => (
            <Card key={opt.value} className="border-border">
              <CardContent className="p-4 flex items-center gap-3">
                <opt.icon className={`w-8 h-8 ${opt.color}`} />
                <div>
                  <p className="text-2xl font-bold text-foreground">{summary[opt.value]}</p>
                  <p className="text-sm text-muted-foreground">{opt.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Attendance Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : classes.length === 0 ? (
        <Card className="border-border">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No classes assigned to you</p>
          </CardContent>
        </Card>
      ) : !selectedClass ? (
        <Card className="border-border">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Select a class to view attendance</p>
          </CardContent>
        </Card>
      ) : needsSection && !selectedSection ? (
        <Card className="border-border">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Select a section to view attendance</p>
          </CardContent>
        </Card>
      ) : students.length === 0 ? (
        <Card className="border-border">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No students found in this class</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead className="text-muted-foreground">Roll No</TableHead>
                  <TableHead className="text-muted-foreground">Student Name</TableHead>
                  <TableHead className="text-muted-foreground">Admission No</TableHead>
                  <TableHead className="text-muted-foreground">Status</TableHead>
                  <TableHead className="text-muted-foreground">Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {students.map((student, index) => (
                  <TableRow key={student.id} className="border-border">
                    <TableCell className="text-foreground">{student.roll_no || index + 1}</TableCell>
                    <TableCell className="text-foreground">
                      <span className="inline-flex items-center gap-2 flex-wrap">
                        {student.first_name} {student.last_name}
                        <BranchLabel name={student.branch_name} show={showBranchLabels} />
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{student.admission_no}</TableCell>
                    <TableCell>
                      {canEditAttendance ? (
                        <div className="flex gap-2">
                          {statusOptions.map((opt) => (
                            <Button
                              key={opt.value}
                              variant={attendance[student.id] === opt.value ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handleStatusChange(student.id, opt.value)}
                              className={`${attendance[student.id] === opt.value
                                ? opt.value === 'present' ? 'bg-green-600'
                                  : opt.value === 'absent' ? 'bg-red-600'
                                  : opt.value === 'late' ? 'bg-amber-600'
                                  : 'bg-blue-600'
                                : 'bg-muted text-muted-foreground'}`}
                            >
                              <opt.icon className="w-4 h-4" />
                            </Button>
                          ))}
                        </div>
                      ) : (
                        (() => {
                          const status = attendance[student.id];
                          const opt = statusOptions.find((o) => o.value === status);
                          if (!opt) {
                            return <span className="text-muted-foreground text-sm">Not marked</span>;
                          }
                          return (
                            <span className="inline-flex items-center gap-1.5 text-sm text-foreground capitalize">
                              <opt.icon className={`w-4 h-4 ${opt.color}`} />
                              {opt.label}
                            </span>
                          );
                        })()
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {(() => {
                        const meta = attendanceMeta[student.id];
                        if (!meta?.source) return '—';
                        const label = meta.source === 'biometric' ? 'Biometric' : meta.source === 'system' ? 'System' : 'Manual';
                        if (meta.source === 'biometric' && meta.first_punch_at) {
                          try {
                            const t = new Date(String(meta.first_punch_at).replace(' ', 'T'));
                            return `${label} · ${t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                          } catch {
                            return label;
                          }
                        }
                        return label;
                      })()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Attendance;
